import { useEffect, useMemo, useState } from "react";
import { createExternalServices } from "@/common/services/external-services.api";
import LabeledInput from "@/common/components/form/LabeledInput";
import { maskLicensePlate } from "@/common/helpers/mask.helpers";
import { useToastContext } from "@/common/components/ui/toast/toast.context";
import LabeledTextarea from "@/common/components/form/LabeledTextarea";
import ButtonWithLoader from "@/common/components/ui/loading/ButtonLoading";
import { ISupplier } from "@/types/supplier.types";
import { getAllSuppliers } from "@/common/services/supplier.api";
import { ImCheckboxChecked, ImCheckboxUnchecked } from "react-icons/im";

function formatCurrencyBR(value: string | number) {
  const num = typeof value === "string" ? value.replace(/\D/g, "") : String(value);
  const parsed = (Number(num) / 100).toFixed(2);
  return parsed
    .replace(".", ",")
    .replace(/\B(?=(\d{3})+(?!\d))/g, ".");
}

type Props = {
  onSubmit: () => void
}

export default function FormExternalServices(props: Props) {
  const { notifySuccess, notifyError } = useToastContext();
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    licensePlate: "",
    description: "",
    totalValue: "0,00",
    supplierName: "",
    pix: ""
  });
  const [suppliers, setSuppliers] = useState<ISupplier[]>([]);
  const [enableLicensePlate, setEnableLicensePlate] = useState(false);
  const [files, setFiles] = useState<FileList>();

  const update = (k: keyof typeof form, v: string) =>
    setForm((f) => ({
      ...f,
      [k]: k === "totalValue" ? Number(v) : v,
    }));

  const handleTotalValueChange = (raw: string) => {
    const onlyDigits = raw.replace(/\D/g, "");
    const formatted = onlyDigits ? formatCurrencyBR(onlyDigits) : "0,00";
    setForm((f) => ({ ...f, totalValue: formatted }));
  };

  const isValid = useMemo(() => {
    return Boolean(
      ((form.licensePlate?.trim() && enableLicensePlate) || (!form.licensePlate?.trim() && !enableLicensePlate)) &&
      form.totalValue.trim() &&
      form.totalValue.trim() !== "0,00" &&
      form.description?.trim() &&
      form.supplierName.trim() && 
      files?.length
    );
  }, [form, enableLicensePlate, files]);

  const handleSave = async () => {
    if (!isValid) {
      alert("Preencha Nome, Placa e O.S.");
      return;
    }

    const payload = {
      licensePlate: form.licensePlate.trim().toUpperCase(),
      description: form.description?.trim() || "",
      totalValue: parseFloat(form.totalValue.replace(/\./g, "").replace(",", ".")),
      supplierName: form.supplierName,
      pix: form.pix
    };

    try {
      setSaving(true);
      const resp = await createExternalServices(
        payload,
        files
      );

      if (resp?.ok && resp?.data) {
        notifySuccess(resp?.message || "Ordem de serviço cadastrada com sucesso!");
        setForm({
          licensePlate: "",
          description: "",
          totalValue: "0,00",
          supplierName: "",
          pix: ""
        });

        props.onSubmit();
      } else {
        console.error("Resposta inesperada:", resp);
        notifyError(resp.message as string)
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      console.error("Erro ao salvar ordem de serviço:", error);
      alert(`Erro ao salvar ordem de serviço: ${error?.message || "desconhecido"}`);
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    async function load() {
      const res = await getAllSuppliers();
      setSuppliers(res.data);
    }
    load();
  }, []);

  return (
    <div >
      <div className="mx-auto bg-white overflow-hidden print:shadow-none max-w-[1100px]">
        <div className="relative">
          <div className=" mb-5 border-neutral-200 relative z-10">
            <label>Fornecedores:</label>
            <select
              style={{ minWidth: "100%", maxWidth: "100%", padding: "10px" }}
              onChange={(e) => update("supplierName", e.target.value)}
            >
              {suppliers && [{ _id: "none", companyName: "" }].concat(suppliers).map(supplier => (
                <option key={supplier._id} value={supplier.companyName}>{supplier.companyName}</option>
              ))}
            </select>

            <div className="mt-6 grid mt-2">
              <div>
                <label htmlFor={`isEligibleSuaVibe`} className="block text-sm font-medium text-gray-600">
                  {enableLicensePlate ? (
                    <div className="flex gap-2 items-center justify-start text-greenPrimary">
                      <ImCheckboxChecked />
                      <span>Veículo com placa ?</span>
                    </div>
                  ) : (
                    <div className="flex gap-2 items-center justify-start">
                      <ImCheckboxUnchecked />
                      <span>Veículo com placa ?</span>
                    </div>
                  )}
                  <input
                    id="isEligibleSuaVibe"
                    name="isEligibleSuaVibe"
                    type="checkbox"
                    checked={enableLicensePlate}
                    onChange={() => {
                      setEnableLicensePlate(!enableLicensePlate);
                    }}
                    className="border p-2 rounded w-full absolute opacity-0"
                  />
                </label>
              </div>
            </div>

            <div className="mt-6 grid grid-cols-1 md:grid-cols-4 gap-4  mt-2">

              <LabeledInput enable={!enableLicensePlate} label="Placa" required={true} value={form.licensePlate} mask={maskLicensePlate} onChange={(v) => update("licensePlate", v.toUpperCase())} />
              <LabeledInput
                label="TOTAL (R$)"
                value={form.totalValue}
                required={true}
                onChange={handleTotalValueChange}
              />
              <LabeledInput
                label="Pix"
                value={form.pix}
                required={true}
                onChange={(v) => update("pix", v)}
              />
            </div>
          </div>

          <div>
            <div>
              <LabeledTextarea
                required={true}
                label="Detalhes da O.S."
                value={form.description}
                onChange={(v) => update("description", v)}
                rows={6} />
            </div>
            <div className="px-8 mt-8">
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-end gap-2 w-full">

              </div>
            </div>

            <input onChange={event => {
              if (event.target.files) {
                setFiles(event.target.files);
              }
            }} className="billetFile" type="file" multiple></input>

            <div className="px-8 pb-10 mt-8 flex justify-end">
              <div className=" flex justify-end">
                <ButtonWithLoader
                  onClick={handleSave}
                  isLoading={saving}
                  disabled={saving || !isValid}
                >
                  Salvar
                </ButtonWithLoader>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @media print {
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .print\\:hidden { display: none !important; }
          .shadow-xl, .shadow { box-shadow: none !important; }
          .bg-neutral-100 { background: #ffffff !important; }
        }
        input::placeholder { color: rgb(163 163 163); }
      `}</style>

    </div>
  );
}
